import { useAuthStoreShallow } from "@/features/authentication";
import { useUpdateSlideMutation } from "@api/slides-library";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import Switch from "@components/ui/switch";
import Tooltip from "@components/ui/tooltip";
import { useAccountStore } from "@features/account-management";
import { cn } from "@lib/cn";
import {
  IconCrown,
  IconDots,
  IconEdit,
  IconFlame,
  IconFolderSymlink,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import {
  isGlobalLibraryOrg,
  useSlidesLibraryStore,
} from "@modules/slides-library/shared";

import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { type ComponentType } from "react";
import type { TSavedSlide } from "@/shared/types";
import { SlideDeleteDialog } from "../dialogs/slide-delete-dialog";
import { SlideMoveDialog } from "../dialogs/slide-move-dialog";
import { SlideRenameDialog } from "../dialogs/slide-rename-dialog";
import { SlideWhenToUseDialog } from "../dialogs/slide-when-to-use-dialog";

const ACTIONS = [
  { icon: IconEdit, label: "Rename", action: "edit" },
  { icon: IconFolderSymlink, label: "Move to a folder", action: "move" },
  { icon: IconTrash, label: "Delete", action: "remove" },
] as const;

const CURATION_FLAGS = [
  { icon: IconFlame, label: "Is popular", key: "isPopular" },
  { icon: IconCrown, label: "Is pro", key: "isPro" },
] as const;

type TAction = (typeof ACTIONS)[number]["action"];
type TCurationFlag = (typeof CURATION_FLAGS)[number]["key"];

export const SingleSlideActions = ({
  slide,
  isOpen,
  onOpenChange,
}: {
  slide: TSavedSlide;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const type = useSlidesLibraryStore((s) => s.type);
  const cannotDeleteTeamSlide = type === "team-slides" && isUserOrCreatorLite;
  const orgId = useAccountStore((s) => s.account?._id);
  const [updateSlide] = useUpdateSlideMutation();
  const isCurationOrg = isGlobalLibraryOrg(orgId);

  const handleAction = (action: TAction) => {
    if (action === "remove" && cannotDeleteTeamSlide) return;

    const actions: Record<
      TAction,
      {
        id: string;
        content: ComponentType<{ slide: TSavedSlide }>;
        props: { slide: TSavedSlide };
      }
    > = {
      edit: {
        id: "rename-slide-dialog",
        content: SlideRenameDialog,
        props: { slide },
      },
      move: {
        id: "move-slide-dialog",
        content: SlideMoveDialog,
        props: { slide },
      },
      remove: {
        id: "delete-slide-dialog",
        content: SlideDeleteDialog,
        props: { slide },
      },
    };
    dispatch(openDialog(actions[action]));
  };

  // Curator guidance for the presentation generator. Not one of ACTIONS —
  // it is curation-org only, like the flags below it.
  const handleWhenToUse = () => {
    dispatch(
      openDialog({
        id: SlideWhenToUseDialog.id,
        content: SlideWhenToUseDialog,
        props: { slide },
      }),
    );
  };

  const handleCurationFlagToggle = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    // Keep the menu open so both flags can be toggled in one go.
    event.preventDefault();
    const key = event.currentTarget.dataset.curationFlag as
      | TCurationFlag
      | undefined;
    if (!key) return;
    updateSlide({ id: slide._id, [key]: !slide[key] });
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "tw-bg-transparent tw-invisible tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-p-0 tw-opacity-0 tw-outline-none tw-transition-[opacity,background-color] hover:tw-bg-blue-100 focus:tw-visible focus:tw-opacity-100 group-focus-within/slide:tw-visible group-focus-within/slide:tw-opacity-100 group-hover/slide:tw-visible group-hover/slide:tw-opacity-100",
            isOpen && "!tw-visible !tw-bg-blue-100 !tw-opacity-100",
          )}
        >
          <IconDots size={14} stroke={1} className="tw-text-blue-800" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="tw-w-40 tw-p-1">
        {ACTIONS.map((action) => {
          const ActionIcon = action.icon;
          const isDisabled =
            action.action === "remove" && cannotDeleteTeamSlide;

          const item = (
            <DropdownMenuItem
              key={action.action}
              disabled={isDisabled}
              className={cn(
                "tw-h-8 tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-[14px] tw-text-blue data-[highlighted]:tw-bg-blue-50",
                isDisabled &&
                  "data-[highlighted]:tw-bg-transparent tw-cursor-not-allowed tw-opacity-40",
              )}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  return;
                }
                handleAction(action.action);
              }}
            >
              <ActionIcon
                className="tw-size-4 tw-shrink-0 tw-text-blue"
                stroke={1}
              />
              {action.label}
            </DropdownMenuItem>
          );

          if (isDisabled) {
            return (
              <Tooltip key={action.action} placement="right">
                <Tooltip.Trigger asChild>{item}</Tooltip.Trigger>
                <Tooltip.Content className="tw-z-[99999]">
                  <Tooltip.Text>
                    Your role doesn&apos;t allow deleting team slides
                  </Tooltip.Text>
                </Tooltip.Content>
              </Tooltip>
            );
          }

          return item;
        })}
        {isCurationOrg && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="tw-h-8 tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-[14px] tw-text-blue data-[highlighted]:tw-bg-blue-50"
              onClick={handleWhenToUse}
            >
              <IconSparkles
                className="tw-size-4 tw-shrink-0 tw-text-blue"
                stroke={1}
              />
              When to use
              {slide.whenToUse ? (
                <span
                  aria-label="has a description"
                  className="tw-ml-auto tw-size-1.5 tw-shrink-0 tw-rounded-full tw-bg-violet-700"
                />
              ) : null}
            </DropdownMenuItem>
            {CURATION_FLAGS.map(({ icon: FlagIcon, label, key }) => (
              <DropdownMenuItem
                key={key}
                data-curation-flag={key}
                className="tw-h-8 tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-[14px] tw-text-blue data-[highlighted]:tw-bg-blue-50"
                onClick={handleCurationFlagToggle}
              >
                <FlagIcon
                  className="tw-size-4 tw-shrink-0 tw-text-blue"
                  stroke={1}
                />
                {label}
                <Switch.Toggle
                  size="sm"
                  checked={!!slide[key]}
                  className="tw-pointer-events-none tw-ml-auto"
                />
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
